import React, { useState, useRef } from "react";
import {
  Dialog,
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  IconButton,
  Divider,
  Chip,
  CircularProgress,
} from "@mui/material";
import { ArrowBackIos, ArrowForwardIos,Storefront } from "@mui/icons-material";
import { useRestaurant } from "../../contexts/RestaurantContext";
import clementImage from '../../static/clement.png';
import railmall from '../../static/Railmall.jpg';
import BusinessHoursDisplay from '../common/BusineesHours'

const OutletSelectionModal = ({ open, onClose, onOutletSelect }) => {
  const [selectedOutlet, setSelectedOutlet] = useState(null);
  const [previousSelectedOutlet, setPreviousSelectedOutlet] = useState(0)
  const scrollRef = useRef();

  const {
    handleOutletSelection,
    outlets,
    outletsLoading,
  } = useRestaurant();

const handleScroll = (direction) => {
    const scrollAmount = 600;
    if (scrollRef.current) {
      scrollRef.current.scrollBy({
        left: direction === "left" ? -scrollAmount : scrollAmount,
        behavior: "smooth",
      });
    }
  };

  const handleSelect = (outlet) => {
    if (selectedOutlet && previousSelectedOutlet !== outlet.id) {
      // console.log("ID Goes Here ")
      localStorage.removeItem('cart_items')
    }

    if (!selectedOutlet) setPreviousSelectedOutlet(0)
    else setPreviousSelectedOutlet(selectedOutlet.id)

    setSelectedOutlet(outlet);
    onOutletSelect?.(outlet);
    onClose();
  };

  const getOutletServices = (outlet) => {
    const services = [];
    if (outlet.is_delivery_available || outlet.delivery_enabled) services.push("Delivery");
    if (outlet.is_takeaway_available || outlet.takeaway_enabled) services.push("Takeaway");
    if (outlet.is_dine_in_available || outlet.dine_in_enabled) services.push("Dine-in");
    if (outlet.is_table_reservation_available) services.push("Table Reservations");
    // if (services.length === 0) services.push("Delivery", "Takeaway");
    return services;
  };

  // Get outlet image based on outlet name or index
  const getOutletImage = (outlet, index) => {
    if (outlet.name?.toLowerCase().includes('clement')) return railmall;
    if (outlet.name?.toLowerCase().includes('rail')) return railmall;
    // Fallback to alternating images
    return index % 2 === 0 ? railmall : railmall;
  };

  // Static rating data
  const getStaticRating = (outlet, index) => {
    // You can customize these based on outlet name or use index
    const ratings = [
      { rating: 4.1, reviews: 555 },
      { rating: 4.1, reviews: 342 },
    ];
    return ratings[index % ratings.length] || ratings[0];
  };

  return (
    <Dialog
      open={open}
      // onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          overflow: "hidden",
          backgroundColor: "#f8f8f8",
          p: { xs: 2, sm: 4, md: 5 },
          maxWidth: "1100px !important",
          mx: { xs: 2, sm: 3 },
          my: { xs: 2, sm: 3 },
        },
      }}
    >
      {/* Title Section */}
      <Box sx={{ textAlign: "center", mb: { xs: 3, sm: 4, md: 5 }, overflow: 'visible !important' }}>
        <Typography
          sx={{
            fontWeight: 500,
            color: "#680000",
            textTransform: "uppercase",
            letterSpacing: { xs: 1, sm: 2 },
            mb: 1,
            fontSize: { xs: "20px !important", sm: "25px !important", md: "24px !important" },
            fontFamily: "Playfair Display !important",
            lineHeight: 1.2,
          }}
        >
          SELECT LOCATION
        </Typography>
        <Typography
          sx={{
            fontWeight: 400,
            color: "#1A1A1A",
            fontSize: { xs: "14px !important", sm: "15px !important", md: "16px !important" },
            lineHeight: 1.4,
          }}
        >
          Choose your preferred outlet to continue
        </Typography>
      </Box>

      {/* Loading State */}
      {outletsLoading ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            py: { xs: 6, sm: 8 },
            gap: 2,
          }}
        >
          <CircularProgress size={48} sx={{ color: "#7B0000" }} />
          <Typography
            variant="h6"
            color="text.secondary"
            sx={{ fontSize: { xs: "16px !important", sm: "18px !important" } }}
          >
            Loading outlets...
          </Typography>
        </Box>
      ) : outlets?.length > 0 ? (
        <Box sx={{ position: "relative", px: { xs: 1, sm: 4, md: 6 } }}>
          {/* Left Arrow - Hidden on mobile */}
          <IconButton
            onClick={() => handleScroll("left")}
            sx={{
              position: "absolute",
              left: { xs: -5, sm: 0 },
              top: "50%",
              transform: "translateY(-50%)",
              backgroundColor: "#680000",
              color: "#fff",
              width: { xs: 40, sm: 50 },
              height: { xs: 40, sm: 50 },
              "&:hover": { backgroundColor: "#5a0000" },
              boxShadow: 3,
              zIndex: 2,
              display: 'flex',
            }}
          >
            <ArrowBackIos sx={{ fontSize: { xs: 16, sm: 20 }, ml: 1 }} />
          </IconButton>

          {/* Outlet Cards Carousel */}
          <Box
            ref={scrollRef}
            sx={{
              display: "flex",
              overflowX: "auto",
              scrollBehavior: "smooth",
              gap: { xs: 2, sm: 3, md: 4 },
              px: { xs: 1, sm: 2, md: 4 },
              pb: 2,
              "&::-webkit-scrollbar": { display: "none" },
              scrollbarWidth: "none",
            }}
          >
            {outlets.map((outlet, index) => {
              const services = getOutletServices(outlet);
              const isSelected = selectedOutlet?.id === outlet.id;
              const staticRating = getStaticRating(outlet, index);
              const outletImage = getOutletImage(outlet, index);
              return (
                <Card
                  key={outlet.id}
                  sx={{
                    flex: { xs: "0 0 300px", sm: "0 0 400px", md: "0 0 520px" },
                    // minHeight: { xs: "420px", sm: "460px", md: "300px" },    
                    height: '450px',
                    maxWidth: '370px !important ',
                    borderRadius: { xs: "12px", sm: "16px" },
                    border: isSelected
                      ? "3px solid #7B0000"
                      : "2px solid #e0e0e0",
                    boxShadow: isSelected
                      ? "0 8px 24px rgba(123, 0, 0, 0.2)"
                      : "0 2px 8px rgba(0,0,0,0.08)",
                    transition: "all 0.3s ease",
                    cursor: "pointer",
                    backgroundColor: "#fff",
                    "&:hover": {
                      borderColor: "#7B0000",
                      boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                    },
                  }}
                  onClick={() => {
                    // console.log("selected Outlet => ", outlet);
                    setSelectedOutlet(outlet)

                  }}
                >
                  <CardContent sx={{
                    p: { xs: 2, sm: 3, md: 4 },
                    height: "100%",
                    display: "flex",
                    flexDirection: "column"
                  }}>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        justifyContent: "space-between",
                        alignItems: { xs: "flex-start", sm: "flex-start" },
                        mb: { xs: 2, sm: 3, md: 0 },
                        gap: { xs: 2, sm: 0 },
                      }}
                    >
                      <Box sx={{ flex: 1, pr: { xs: 0, sm: 2 } }}>
                        <Typography
                          sx={{
                            fontWeight: 600,
                            color: "#680000",
                            fontSize: { xs: "18px !important", sm: "19px !important", md: "20px !important" },
                            mb: { xs: 1, sm: 1.5 },
                            lineHeight: 1.3,
                          }}
                        >
                          {outlet.name}
                        </Typography>
                        <Typography
                          sx={{
                            color: "#444444",
                            fontSize: { xs: "0.85rem !important", sm: "0.9rem !important", md: "0.95rem !important" },
                            lineHeight: 1.6,
                            mb: { xs: 1.5, sm: 2 },
                          }}
                        >
                          {outlet.address}
                        </Typography>
                        <Typography
                          sx={{
                            color: "#444444",
                            fontSize: { xs: "0.85rem !important", sm: "0.9rem !important", md: "0.95rem !important" },
                            lineHeight: 1.6,
                            mb: { xs: 1.5, sm: 2 },
                          }}
                        >
                          {outlet.state} - {outlet.pincode}

                        </Typography>


                        {/* Rating - Using Static Data */}
                        {/* <Box sx={{ display: "flex", alignItems: "center", mb: { xs: 2, sm: 3 } }}>
                          <Box sx={{ display: "flex", gap: 0.3 }}>
                            {[...Array(5)].map((_, i) => (
                              <Typography
                                key={i}
                                sx={{
                                  color: i < Math.floor(staticRating.rating) ? "#FFD700" : "#e0e0e0",
                                  fontSize: { xs: "1.2rem !important", sm: "1.4rem !important", md: "1.5rem !important" },
                                }}
                              >
                                ★
                              </Typography>
                            ))}
                          </Box>
                          <Typography
                            sx={{
                              fontSize: { xs: "0.85rem !important", sm: "0.95rem !important", md: "1rem !important" },
                              color: "#680000",
                              fontWeight: 600,
                              ml: { xs: 1, sm: 1.5 },
                            }}
                          >
                            {staticRating.rating} | {staticRating.reviews} reviews
                          </Typography>
                        </Box> */}
                      </Box>

                      {/* Outlet Image - Updated to round shape */}
                      <Box
                        sx={{
                          width: { xs: "75px", sm: "75px", md: 100 },
                          height: { xs: "75px", sm: "75px", md: 100 },
                          borderRadius: "50%",
                          objectFit: "cover",
                          boxShadow: 2,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          overflow: "hidden",
                          backgroundColor: "#f5f5f5",
                        }}
                      >
                        {outlet.image_url ? (
                          <Box
                            component="img"
                            src={outlet.image_url}
                            alt={outlet.name}
                            sx={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                          />
                        ) : (
                          <Storefront
                            sx={{
                              fontSize: { xs: 40, sm: 40, md: 55 },
                              color: "text.secondary",
                            }}
                          />
                        )}
                      </Box>
                    </Box>

                    <Divider sx={{ my: { xs: 2, sm: 2.5, md: 1 }, borderColor: "#e0e0e0" }} />

                    <Box sx={{ flex: 1 }}>

                      {services.length > 0 &&
                        <>
                          <Typography
                            sx={{
                              fontWeight: 600,
                              fontSize: { xs: "16px !important", sm: "17px !important", md: "12px !important" },
                              mb: { xs: 1, sm: 1.5 },
                              color: "#000000",
                            }}
                          >
                            Available Services:
                          </Typography>
                          <Box sx={{
                            display: "flex",
                            gap: { xs: 1, sm: 1.5 },
                            flexWrap: "wrap",
                            mb: { xs: 2, sm: 3 }
                          }}>
                            {services.map((service) => (
                              <Chip
                                key={service}
                                label={service}
                                variant="outlined"
                                sx={{
                                  borderColor: "#680000",
                                  color: "#680000",
                                  fontSize: { xs: "0.8rem !important", sm: "0.85rem !important", md: "10px !important" },
                                  height: { xs: 28, sm: 32, md: 15 },
                                  fontWeight: 300,
                                  px: 1,
                                  borderWidth: 1.5,
                                  backgroundColor: "#FFF0F0",
                                  "& .MuiChip-label": {
                                    fontSize: {
                                      xs: "0.65rem",
                                      sm: "0.7rem",
                                      md: "10px",
                                    },
                                    px: "6px",
                                  },
                                }}
                              />
                            ))}
                          </Box>

                          <Divider sx={{ my: { xs: 2, sm: 2.5, md: 1 }, borderColor: "#e0e0e0" }} />
                        </>
                      }

                      <Typography
                        sx={{
                          fontWeight: 600,
                          fontSize: { xs: "16px !important", sm: "17px !important", md: "12px !important" },
                          mb: { xs: 1, sm: 1.5 },
                          color: "#000000",
                        }}
                      >
                        Timings:
                      </Typography>
                      <BusinessHoursDisplay business_hours={outlet.business_hours} />
                      {/* <Typography sx={{
                        fontSize: { xs: "0.85rem !important", sm: "0.95rem !important", md: "1rem !important" },
                        color: "#444444",
                        mb: 0.5
                      }}>
                        Lunch : 11.00 AM to 03.00 PM
                      </Typography>
                      <Typography sx={{
                        fontSize: { xs: "0.85rem !important", sm: "0.95rem !important", md: "1rem !important" },
                        color: "#444444",
                        mb: { xs: 2, sm: 3 }
                      }}>
                        Dinner : 05.00 PM to 11.00 PM
                      </Typography> */}
                    </Box>

                    <Button
                      fullWidth
                      variant="contained"
                      onClick={(e) => {
                        e.stopPropagation();
                        // console.log("Selected Outlet =>", outlet);
                        handleSelect(outlet);
                        // localStorage.removeItem('cart_items')
                        // window.location.reload()
                      }}
                      sx={{
                        backgroundColor: "#680000",
                        color: "#fff",
                        borderRadius: { xs: "6px", sm: "8px" },
                        textTransform: "none",
                        fontWeight: 600,
                        fontSize: { xs: "0.9rem !important", sm: "0.95rem !important", md: "12px !important" },
                        py: { xs: 1.25, sm: 1.5 },
                        boxShadow: "0 4px 12px rgba(123, 0, 0, 0.3)",
                        "&:hover": {
                          backgroundColor: "#5a0000",
                          boxShadow: "0 6px 16px rgba(123, 0, 0, 0.4)",
                        },
                        mt: "auto",
                      }}
                    >
                      Select outlet
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </Box>

          {/* Right Arrow - Hidden on mobile */}
          <IconButton
            onClick={() => handleScroll("right")}
            sx={{
              position: "absolute",
              right: { xs: -5, sm: 0 },
              top: "50%",
              transform: "translateY(-50%)",
              backgroundColor: "#680000",
              color: "#fff",
              width: { xs: 40, sm: 50 },
              height: { xs: 40, sm: 50 },
              "&:hover": { backgroundColor: "#5a0000" },
              boxShadow: 3,
              zIndex: 2,
              display: 'flex',
            }}
          >
            <ArrowForwardIos sx={{ fontSize: { xs: 16, sm: 20 } }} />
          </IconButton>

          {/* Mobile Navigation Dots */}
          {outlets?.length > 1 && (
            <Box
              sx={{
                display: { xs: 'flex', sm: 'none' },
                justifyContent: 'center',
                gap: 1,
                mt: 2,
                mb: 1
              }}
            >
              {outlets.map((_, index) => (
                <Box
                  key={index}
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: index === outlets.findIndex(outlet => outlet.id === selectedOutlet?.id)
                      ? '#680000'
                      : '#d6b02b',
                    transition: 'background-color 0.3s ease'
                  }}
                />
              ))}
            </Box>
          )}
        </Box>
      ) : (
        <Box sx={{ textAlign: "center", py: { xs: 4, sm: 5 } }}>
          <Typography
            sx={{
              color: "text.secondary",
              fontSize: { xs: "16px !important", sm: "18px !important", md: "1.1rem !important" }
            }}
          >
            No outlets available.
          </Typography>
        </Box>
      )}
    </Dialog>
  );
};

export default OutletSelectionModal;